import { z } from 'zod';
import { id, isoDateTime, localDate, localDateTime } from './common.js';
/**
 * Medication tracking.
 *
 * Scope, deliberately: this records what you decided to take and how the day
 * went. It does not dose, titrate, warn about interactions, or recommend
 * anything. The reference table exists so you can pick "guanfacine" from a list
 * instead of typing it, not to advise you — every clinical decision stays
 * between you and your prescriber.
 */
export const medClass = z.enum([
    'stimulant_methylphenidate',
    'stimulant_amphetamine',
    'non_stimulant_alpha2',
    'non_stimulant_nri',
    'antidepressant_off_label',
    'wakefulness',
    'supplement',
    'other',
]);
export const doseUnit = z.enum(['mg', 'mcg', 'ml', 'tablet', 'capsule', 'patch', 'other']);
export const scheduleTime = z.object({
    /** 24h local time, `HH:MM`. */
    time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    label: z.string().max(40).optional(),
});
export const createMedication = z.object({
    /** Reference key when picked from the list; null for a free-text entry. */
    refKey: z.string().max(60).nullable().default(null),
    name: z.string().min(1).max(120),
    doseAmount: z.number().positive().max(10000).nullable().default(null),
    doseUnit: doseUnit.default('mg'),
    /** e.g. daily at 08:00 and 13:00. Drives the "did you take it?" prompts. */
    schedule: z.array(scheduleTime).max(8).default([]),
    /** Days of week the schedule applies, 0=Sun. Empty means every day. */
    daysOfWeek: z.array(z.number().int().min(0).max(6)).max(7).default([]),
    startedOn: localDate.nullable().default(null),
    stoppedOn: localDate.nullable().default(null),
    isActive: z.boolean().default(true),
    notes: z.string().max(2000).nullable().default(null),
});
export const updateMedication = createMedication.partial();
export const doseStatus = z.enum(['taken', 'missed', 'skipped', 'late']);
export const logDose = z.object({
    medicationId: id,
    /**
     * The slot this fills, as a local wall-clock time — omit for an unscheduled
     * (PRN) dose. This is the exact string `/medications/today` emits for each
     * slot, and it doubles as the slot's identity in the partial unique index.
     */
    scheduledFor: localDateTime.nullable().default(null),
    /** When it was actually taken — a real instant, so fully qualified. */
    takenAt: isoDateTime.nullable().default(null),
    status: doseStatus,
    note: z.string().max(500).nullable().default(null),
    /**
     * How it landed — "jittery", "locked in", "flat". Short tags rather than
     * prose so the question "do the jittery days cluster?" is answerable later.
     * Empty means nothing was said, which is not the same as "it was fine".
     */
    feelings: z.array(z.string().min(1).max(30)).max(8).default([]),
});
/** Every self-rated scale on a check-in, in the order a person reads them. */
export const moodMetric = z.enum([
    'mood',
    'energy',
    'focus',
    'restlessness',
    'overwhelm',
    'irritability',
    'rejectionSensitivity',
    'anxiety',
]);
//# sourceMappingURL=medication.js.map